/**
 * Adaptation des `boosterReferences` API pour le tableau boosters de la page Programme (client).
 */
import type {
  BoosterBoosterReadModel,
  BoosterReferenceBoosterReadModel
} from '@r/api/generated/client.schemas.ts';

export interface BoosterProgramTableRow {
  rowKey: string;
  brand: string;
  productLine?: string | null;
  name: string;
  uniqueCode: string;
  pointValue: number;
  boostedPointValue: number;
  minQuantity: number;
  coefficient: number;
}

/** Points boostés : valeur API si présente, sinon calcul local. */
function getBoostedPointValue(entry: BoosterReferenceBoosterReadModel) {
  if (entry.boostedPointValue != null) {
    return entry.boostedPointValue;
  }

  return entry.reference.pointValue * entry.coefficient;
}

/** Une ligne de tableau par palier (référence × quantité min × coefficient). */
export function getBoosterProgramTableRows(
  booster: BoosterBoosterReadModel
): BoosterProgramTableRow[] {
  const rows = (booster.boosterReferences ?? []).map((entry, index) => ({
    rowKey: `${entry.id ?? entry.reference.id}-${entry.minQuantity ?? 1}-${index}`,
    brand: entry.reference.brand,
    productLine: entry.reference.productLine,
    name: entry.reference.name,
    uniqueCode: entry.reference.uniqueCode,
    pointValue: entry.reference.pointValue,
    boostedPointValue: getBoostedPointValue(entry),
    minQuantity: entry.minQuantity ?? 1,
    coefficient: entry.coefficient
  }));

  return rows.sort((left, right) => {
    const brandCompare = left.brand.localeCompare(right.brand);
    if (brandCompare !== 0) {
      return brandCompare;
    }

    const codeCompare = left.uniqueCode.localeCompare(right.uniqueCode);
    if (codeCompare !== 0) {
      return codeCompare;
    }

    return left.minQuantity - right.minQuantity;
  });
}

export type BoosterMultiplierSummary =
  | { type: 'single'; coefficient: number }
  | { type: 'variable' };

/** Texte d’intro : coefficient unique ou paliers variables. */
export function getBoosterMultiplierSummary(
  booster: BoosterBoosterReadModel
): BoosterMultiplierSummary {
  const coefficients = [
    ...new Set((booster.boosterReferences ?? []).map((entry) => entry.coefficient))
  ];

  if (coefficients.length === 1) {
    return { type: 'single', coefficient: coefficients[0] ?? 1 };
  }

  return { type: 'variable' };
}
